import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  LINK_TIPPER_DISCORD,
  SEARCH_DISCORD_MEMBERS,
} from "../../../queries/mutation/broadcaster/vipQueries";

type DiscordMember = {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
};

type Props = {
  tipper: { id: string; username: string };
  onClose: () => void;
  onLinked: () => void;
};

const Avatar = ({ member, size }: { member: DiscordMember; size: string }) =>
  member.avatarUrl ? (
    <img
      src={member.avatarUrl}
      alt=""
      className={`${size} rounded-full shrink-0`}
    />
  ) : (
    <div className={`${size} rounded-full bg-base-300 shrink-0`} />
  );

// Picking from the server's members - rather than pasting an id blind - is
// what lets a mod see who they're about to give the VIP role to.
const LinkDiscordModal = ({ tipper, onClose, onLinked }: Props) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<DiscordMember | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading, error } = useQuery(SEARCH_DISCORD_MEMBERS, {
    variables: { query: debounced },
    skip: debounced.length < 2,
    fetchPolicy: "network-only",
  });
  const results: DiscordMember[] =
    debounced.length < 2 ? [] : data?.searchDiscordMembers ?? [];

  const [link, { loading: linking }] = useMutation(LINK_TIPPER_DISCORD);

  const handleLink = async () => {
    if (!picked) return;
    try {
      await link({ variables: { tipperId: tipper.id, discordId: picked.id } });
      toast.success(
        `Linked ${tipper.username} to @${picked.displayName ?? picked.username}`,
      );
      onLinked();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg">
          Link a Discord account to {tipper.username}
        </h3>

        {picked ? (
          <>
            <div className="flex items-center gap-3 my-4 p-3 bg-base-200 rounded-box">
              <Avatar member={picked} size="w-12 h-12" />
              <div className="min-w-0">
                <div className="font-semibold truncate">
                  {picked.displayName ?? picked.username}
                </div>
                <div className="text-sm text-base-content/60 truncate">
                  @{picked.username}
                </div>
              </div>
            </div>
            <p className="text-sm text-base-content/70">
              While {tipper.username} is VIP, this account gets the VIP role.
              Make sure it's really them - if they later link their own
              Reinstorm account, that takes over.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPicked(null)}
                disabled={linking}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleLink}
                disabled={linking}
              >
                {linking ? "Linking..." : "Link"}
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="input input-bordered flex items-center gap-2 mt-4">
              <FaSearch className="opacity-50" />
              <input
                type="search"
                className="grow"
                placeholder="Discord name, or paste a user ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </label>
            <p className="text-xs text-base-content/50 mt-1">
              Searches members of your Discord server.
            </p>

            <div className="mt-3 max-h-72 overflow-y-auto">
              {debounced.length < 2 ? null : loading ? (
                <span className="loading loading-dots loading-sm" />
              ) : error ? (
                <p className="text-sm text-error">{error.message}</p>
              ) : results.length === 0 ? (
                <p className="text-sm text-base-content/60">
                  No members of your server match "{debounced}"
                </p>
              ) : (
                <ul className="space-y-1">
                  {results.map((member) => (
                    <li key={member.id}>
                      <button
                        className="w-full flex items-center gap-3 p-2 rounded-box hover:bg-base-200 text-left"
                        onClick={() => setPicked(member)}
                      >
                        <Avatar member={member} size="w-8 h-8" />
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {member.displayName ?? member.username}
                          </div>
                          <div className="text-xs text-base-content/60 truncate">
                            @{member.username}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="modal-action">
              <button className="btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LinkDiscordModal;
